import { UnitTypeCollection } from '@r6digital/rapidstor-sdk/domain';
import type {
    ListingSettings, SizeCategory, UnitType, UnitTypeSortKey,
} from '@r6digital/rapidstor-sdk/domain';

import type Location from '@r6digital/rapidstor-types/react/entities/Location';
import type RawDescriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';

import { siteFor } from './context';

/** The quick-filter selections the listing offers. */
export type SizeFilter = 'all' | SizeCategory | 'vehicle';

/**
 * The listing, as a list of cards. Each card holds one or more variants of the same descriptor —
 * a flat listing simply yields single-variant cards, so the screen renders one shape either way.
 */
export interface Listing {
    /** Cards to render, in display order. */
    cards: UnitTypeCollection[];
    /** Whether there is nothing to show. */
    isEmpty: boolean;
}

/**
 * Narrows a card's variants by the active quick filter.
 *
 * `all` deliberately applies no availability rule — matching the original listing, where the
 * minimum-availability setting only ever gated the size brackets.
 *
 * @param units - A card's variants.
 * @param listing - The site's listing settings.
 * @param filter - The active quick filter.
 * @returns The matching variants.
 */
const applyFilter = (
    units: UnitTypeCollection,
    listing: ListingSettings,
    filter: SizeFilter,
): UnitTypeCollection => {
    if (filter === 'all') return units;
    if (filter === 'vehicle') return units.where({ vehicleSpace: true });

    return units.where({
        sizeCategory: filter,
        minAvailable: listing.hideBelowMinAvailable() ? listing.minAvailableUnits() : undefined,
    });
};

/**
 * Drops variants that would show the same floor label twice, keeping the best (first) of each.
 *
 * `bestPerFloorGroup` already keeps at most one per *category*, so this only matters where two
 * categories resolve to the same label — which a site's translations can cause.
 *
 * @param units - A card's variants, already sorted.
 * @returns The deduplicated variants.
 */
const dedupeFloorLabels = (units: UnitTypeCollection): UnitTypeCollection => {
    const seen = new Set<string>();
    return units.filter((unitType) => {
        if (seen.has(unitType.floorLabel)) return false;
        seen.add(unitType.floorLabel);
        return true;
    });
};

/**
 * Orders cards by their own best (first) variant, so the listing as a whole reads in sort order.
 *
 * Takes the resolved sort order rather than the site, so building a listing constructs one `Site`
 * wrapper rather than one per card.
 *
 * @param cards - The cards to order. Each holds at least one variant.
 * @param order - The site's configured sort criteria.
 * @returns The ordered cards.
 */
const sortCards = (
    cards: UnitTypeCollection[],
    order: UnitTypeSortKey[],
): UnitTypeCollection[] => {
    const leads = new UnitTypeCollection(
        cards.map((card) => card.first()).filter((unit): unit is UnitType => unit !== undefined),
    );
    const byLead = new Map(cards.map((card) => [card.first(), card]));

    return leads
        .sortBy(order)
        .map((lead) => byLead.get(lead))
        .filter((card): card is UnitTypeCollection => card !== undefined);
};

/**
 * Builds the listing: hydrate, group per the site's merchandising settings, sort, then filter.
 *
 * Mirrors the three configurations the standard listing supports:
 *
 * - **multilevel** — one card per descriptor, holding one variant per floor category.
 * - **grouped descriptors** — one card per descriptor, holding its single best variant.
 * - **flat** — one card per unit type.
 *
 * @param descriptors - Friendly descriptor entities from the journey context.
 * @param location - The friendly location entity.
 * @param filter - The active quick filter.
 * @returns The {@link Listing} to render.
 */
export const buildListing = (
    descriptors: RawDescriptor[],
    location: Location,
    filter: SizeFilter,
): Listing => {
    const site = siteFor(location);
    const listing = site.settings.listing();
    const order = listing.sortOrder();

    // The app transforms at its API boundary, so it holds friendly entities already — this is the
    // entry point for that, rather than re-running the SDK's own transform over a raw payload.
    const { unitTypes } = site.hydrateDescriptors(descriptors);

    let cards: UnitTypeCollection[];

    if (listing.useMultilevel()) {
        cards = sortCards(
            [...unitTypes.groupByDescriptor().values()]
                .map((group) => dedupeFloorLabels(group.bestPerFloorGroup().sortBy(order)))
                .filter((group) => !group.isEmpty),
            order,
        );
    } else if (listing.groupDescriptors()) {
        // One representative per descriptor: the best available variant, else the best overall.
        cards = sortCards(
            [...unitTypes.groupByDescriptor().values()]
                .map((group) => group.sortBy(order).availableFirst().first())
                .filter((unitType): unitType is UnitType => unitType !== undefined)
                .map((unitType) => new UnitTypeCollection([unitType])),
            order,
        );
    } else {
        cards = unitTypes.sortBy(order).map((unitType) => new UnitTypeCollection([unitType]));
    }

    const filtered = cards
        .map((card) => applyFilter(card, listing, filter))
        .filter((card) => !card.isEmpty);

    return { cards: filtered, isEmpty: filtered.length === 0 };
};
